
import { useState, useEffect } from "react";
import { ArrowLeft, Save, Check, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

// Cookie consent preferences type
type ConsentPreferences = {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
};

// Data retention settings type
type RetentionSettings = {
  analysisDataRetention: string;
  accountDataDeletion: "active" | "scheduled" | "none";
  scheduledDeletionDate?: string;
};

// Default preferences with necessary cookies always true
const defaultPreferences: ConsentPreferences = {
  necessary: true,  // Always required
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: Date.now(),
  version: "1.0"
};

// Default retention settings
const defaultRetentionSettings: RetentionSettings = {
  analysisDataRetention: "30days",
  accountDataDeletion: "none"
};

const PrivacyPreferences = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultPreferences);
  const [retentionSettings, setRetentionSettings] = useState<RetentionSettings>(defaultRetentionSettings);
  const [saved, setSaved] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load saved preferences on component mount
  useEffect(() => {
    const storedConsent = localStorage.getItem("cookie-consent");
    if (storedConsent) {
      try {
        const storedPreferences = JSON.parse(storedConsent);
        setPreferences(storedPreferences);
      } catch (e) {
        console.error("Failed to parse stored consent:", e);
      }
    }

    // Load data retention settings
    const storedRetention = localStorage.getItem("data-retention-settings");
    if (storedRetention) {
      try {
        const storedSettings = JSON.parse(storedRetention);
        setRetentionSettings(storedSettings);
      } catch (e) {
        console.error("Failed to parse stored retention settings:", e);
      }
    }
  }, []);

  // Save preferences
  const savePreferences = () => {
    const updatedPreferences = {
      ...preferences,
      timestamp: Date.now()
    };
    
    localStorage.setItem("cookie-consent", JSON.stringify(updatedPreferences));
    localStorage.setItem("data-retention-settings", JSON.stringify(retentionSettings));
    
    // Here you would implement actual cookie/tracking enabling or disabling
    // based on the user's choices
    
    toast({
      title: "Preferences Saved",
      description: "Your privacy and data retention preferences have been updated.",
    });
    
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // Accept all cookies
  const acceptAll = () => {
    const allConsent = {
      ...preferences,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now()
    };
    
    setPreferences(allConsent);
    localStorage.setItem("cookie-consent", JSON.stringify(allConsent));
    
    toast({
      title: "All Cookies Accepted",
      description: "You've enabled all cookie categories.",
    });
    
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // Handle data retention change
  const handleRetentionChange = (value: string) => {
    setRetentionSettings({
      ...retentionSettings,
      analysisDataRetention: value
    });
  };

  // Schedule account deletion
  const scheduleAccountDeletion = () => {
    // Set deletion date to 30 days from now
    const deletionDate = new Date();
    deletionDate.setDate(deletionDate.getDate() + 30);
    
    const updatedSettings = {
      ...retentionSettings,
      accountDataDeletion: "scheduled" as const,
      scheduledDeletionDate: deletionDate.toISOString()
    };
    
    setRetentionSettings(updatedSettings);
    localStorage.setItem("data-retention-settings", JSON.stringify(updatedSettings));
    
    toast({
      title: "Account Deletion Scheduled",
      description: `Your account will be deleted on ${deletionDate.toLocaleDateString()}.`,
    });
  };

  // Cancel scheduled deletion
  const cancelScheduledDeletion = () => {
    const updatedSettings = {
      ...retentionSettings,
      accountDataDeletion: "none" as const,
      scheduledDeletionDate: undefined
    };
    
    setRetentionSettings(updatedSettings);
    localStorage.setItem("data-retention-settings", JSON.stringify(updatedSettings));
    
    toast({
      title: "Deletion Cancelled",
      description: "Your scheduled account deletion has been cancelled.",
    });
  };

  // Delete account immediately
  const deleteAccountImmediately = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to delete your account.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      // In a real implementation, this would delete user data from the database
      // This is a simplified example using Supabase auth
      const { error } = await supabase.auth.admin.deleteUser(user.id);
      
      if (error) throw error;
      
      // Clear all local storage
      localStorage.clear();
      
      toast({
        title: "Account Deleted",
        description: "Your account and all associated data have been deleted.",
      });
      
      // Redirect to home page after a short delay
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({
        title: "Deletion Failed",
        description: "There was a problem deleting your account. Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-fraudeye-primary">
      <div className="container max-w-2xl px-4 py-12">
        <Button variant="outline" size="sm" className="mb-8" asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>
        
        <Card>
          <CardHeader>
            <CardTitle>Privacy & Data Preferences</CardTitle>
            <CardDescription>
              Control how we use cookies, process your data, and manage your data retention settings.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Cookie Preferences</h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-base font-medium">Necessary Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    These cookies are essential for the website to function properly. They cannot be disabled.
                  </p>
                </div>
                <Switch checked={true} disabled />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-base font-medium">Functional Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    These cookies enable the website to provide enhanced functionality and personalization.
                    They may be set by us or by third parties whose services we have added to our pages.
                  </p>
                </div>
                <Switch 
                  checked={preferences.functional} 
                  onCheckedChange={(checked) => setPreferences({...preferences, functional: checked})} 
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-base font-medium">Analytics Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    These cookies allow us to count visits and traffic sources so we can measure and improve
                    the performance of our site. They help us know which pages are the most and least
                    popular and see how visitors move around the site.
                  </p>
                </div>
                <Switch 
                  checked={preferences.analytics} 
                  onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})} 
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-base font-medium">Marketing Cookies</h4>
                  <p className="text-sm text-muted-foreground">
                    These cookies may be set through our site by our advertising partners. They may be used
                    by those companies to build a profile of your interests and show you relevant advertisements
                    on other sites.
                  </p>
                </div>
                <Switch 
                  checked={preferences.marketing} 
                  onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})} 
                />
              </div>
            </div>
            
            <Separator className="my-6" />
            
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Data Retention Settings</h3>
              
              <div className="space-y-2">
                <label className="text-base font-medium">Analysis Data Retention</label>
                <p className="text-sm text-muted-foreground mb-2">
                  Set how long we keep your detection analysis results. This applies to all new analyses going forward.
                </p>
                <Select 
                  value={retentionSettings.analysisDataRetention}
                  onValueChange={handleRetentionChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select retention period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7days">7 days (minimum)</SelectItem>
                    <SelectItem value="30days">30 days</SelectItem>
                    <SelectItem value="90days">90 days</SelectItem>
                    <SelectItem value="1year">1 year</SelectItem>
                    <SelectItem value="forever">Keep indefinitely</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Separator className="my-4" />
              
              <div className="space-y-4">
                <h4 className="text-base font-medium">Account Data Management</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Control what happens to your account data and when it is removed from our systems.
                </p>
                
                {retentionSettings.accountDataDeletion === "scheduled" && (
                  <Alert className="mb-4">
                    <AlertTitle>Account Deletion Scheduled</AlertTitle>
                    <AlertDescription>
                      Your account is scheduled for deletion on{" "}
                      {new Date(retentionSettings.scheduledDeletionDate || "").toLocaleDateString()}.
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2"
                        onClick={cancelScheduledDeletion}
                      >
                        Cancel Deletion
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="flex flex-col space-y-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" className="w-full">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete My Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action will delete your account and all associated data. You can choose to delete it immediately or schedule it for deletion in 30 days.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={scheduleAccountDeletion}
                          className="bg-orange-600 hover:bg-orange-700"
                        >
                          Schedule for 30 Days
                        </AlertDialogAction>
                        <AlertDialogAction
                          onClick={deleteAccountImmediately}
                          className="bg-red-600 hover:bg-red-700"
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Deleting..." : "Delete Now"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    Deleting your account will remove all your personal data, detection results, and settings from our systems.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="rounded-lg bg-muted p-4 mt-6">
              <h4 className="font-medium">Additional Information</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Some cookies are placed by third party services that appear on our pages. You can view our full
                Privacy Policy to learn more about how we use cookies and how we protect your data.
              </p>
              <div className="mt-3">
                <Link to="/privacy-policy" className="text-sm text-primary hover:underline">
                  View Privacy Policy
                </Link>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={acceptAll}>
              Accept All Cookies
            </Button>
            <Button onClick={savePreferences}>
              {saved ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Preferences
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPreferences;
